import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import translate from "@insite/client-framework/Translate";
import { WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import StaticListProductListLine from "@insite/content-library/Widgets/StaticList/StaticListProductListLine";
import { AccordionSectionHeaderProps } from "@insite/mobius/AccordionSection/AccordionSectionHeader";
import { AccordionSectionPanelProps } from "@insite/mobius/AccordionSection/AccordionSectionPanel";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@insite/mobius/Img";
import { Link } from "@insite/mobius/index";
import { Accordion } from "@insite/mobius/index";
import { AccordionSection } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    wishListState: getWishListState(state, state.pages.staticList.wishListId),
    productInfosByWishListLineId: state.pages.staticList.productInfosByWishListLineId,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

interface VerndaleCustomizationStyles {
    lineInnerContainer?: GridContainerProps;
    imageWrapper?: InjectableCss;
    linkWrappingImage?: ClickablePresentationProps;
    img?: ImgProps;
    imageGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    accordionHeader?: AccordionSectionHeaderProps;
    accordionPanel?: AccordionSectionPanelProps;
    accordionSection?: InjectableCss;
}

export interface StaticListProductListStyles extends VerndaleCustomizationStyles {
    mainWrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    linesWrapper?: InjectableCss;
}

export const staticListProductListStyles: StaticListProductListStyles = {
    mainWrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
    countText: {
        weight: 600,
    },
    linesWrapper: {
        css: css`
            margin-top: 10px;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
};

const styles = staticListProductListStyles;

interface LineAccordionLineItemProps {
    wishList: WishListModel;
    accordionLineItem: AccordionLineItem;
    productInfosByWishListLineId: SafeDictionary<ProductInfo>;
}

interface AccordionLineItem {
    lines: WishListLineModel[];
    parentErpNumber: string;
    parentDescription: string;
    parentImage: string;
    parentPath: string;
    parentTitle: string;
}

const LineAccordion: React.FC<LineAccordionLineItemProps> = ({
    accordionLineItem,
    productInfosByWishListLineId,
}: LineAccordionLineItemProps) => {
    const accordionButton: React.ReactNode = (
        <>
            <GridContainer
                {...styles.lineInnerContainer}
                className="wishlist-line-grid-container"
                data-test-selector={`${accordionLineItem.parentErpNumber}`}
            >
                <GridItem {...styles.imageGridItem}>
                    <StyledWrapper {...styles.imageWrapper} className="wishlist-line-product-image-wrapper">
                        <Clickable
                            {...styles.linkWrappingImage}
                            href={`/Product/${accordionLineItem.parentPath}`}
                            data-test-selector="productImage"
                        >
                            <Img
                                {...styles.img}
                                width="209"
                                height="209"
                                src={accordionLineItem.lines[0].smallImagePath}
                                altText={accordionLineItem.parentTitle}
                            />
                        </Clickable>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.descriptionGridItem} className="wishlist-line-description-grid-item">
                    <Link
                        href={`/Product/${accordionLineItem.parentPath}`}
                        alt={`See product details for ${accordionLineItem.parentTitle}`}
                        data-test-selector="productDescriptionLink"
                    >
                        {accordionLineItem.parentTitle}
                    </Link>
                </GridItem>
            </GridContainer>
        </>
    );

    const { lines: childrenLines } = accordionLineItem;

    return (
        <GridContainer {...styles.accordionSection}>
            <Accordion headingLevel={2}>
                <AccordionSection
                    title={accordionButton}
                    headerProps={styles.accordionHeader}
                    panelProps={styles.accordionPanel}
                >
                    {childrenLines
                        .filter(wishListLine => !!productInfosByWishListLineId[wishListLine.id])
                        .map(wishListLine => (
                            <StaticListProductListLine
                                key={wishListLine.id}
                                wishListLine={wishListLine}
                                productInfo={productInfosByWishListLineId[wishListLine.id]!}
                            />
                        ))}
                </AccordionSection>
            </Accordion>
        </GridContainer>
    );
};

const StaticListProductList = ({ wishListState, productInfosByWishListLineId }: Props) => {
    if (wishListState.isLoading || !wishListState.value || Object.keys(productInfosByWishListLineId).length === 0) {
        return null;
    }

    const wishList = wishListState.value;

    const { wishListLineCollection } = wishList;

    const parentErpNumbers = wishListLineCollection!
        .filter(line => line.properties.parentErpNumber !== undefined)
        .map(line => line.properties.parentErpNumber);
    const wishListLines = wishListLineCollection!.filter(line => !line.properties.parentErpNumber);

    const parentErpNumbersWithLines = parentErpNumbers.reduce((acc, erpNumber) => {
        const lines = wishListLineCollection!.filter(line => line.properties.parentErpNumber === erpNumber);
        if (lines.length > 0) {
            acc[erpNumber as keyof typeof acc] = {
                lines,
                parentDescription: lines[0].properties.parentDescription,
                parentErpNumber: lines[0].properties.parentErpNumber,
                parentImage: lines[0].properties.parentImage,
                parentPath: lines[0].properties.parentPath,
                parentTitle: lines[0].properties.parentTitle,
            };
        }
        return acc;
    }, {} as Record<string, AccordionLineItem>);

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <Typography {...styles.countText}>
                {wishList.wishListLinesCount === 1 && translate("{0} Product", wishList.wishListLinesCount.toString())}
                {wishList.wishListLinesCount > 1 && translate("{0} Products", wishList.wishListLinesCount.toString())}
            </Typography>
            <StyledWrapper {...styles.linesWrapper}>
                {Object.keys(parentErpNumbersWithLines).map((erpNumber) => (
                    <LineAccordion
                        key={`${erpNumber}`}
                        wishList={wishList}
                        accordionLineItem={parentErpNumbersWithLines[erpNumber]}
                        productInfosByWishListLineId={productInfosByWishListLineId}
                    />
                ))}

                {wishListLines?.map(wishListLine => {
                     const productInfo = productInfosByWishListLineId[wishListLine.id];
                     if (!productInfo) {
                         return null;
                     }

                    return  (
                        <StaticListProductListLine
                            key={wishListLine.id}
                            wishListLine={wishListLine}
                            productInfo={productInfo}
                        />
                    )
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(StaticListProductList),
    definition: {
        group: "Static List",
        displayName: "Product List",
        allowedContexts: ["StaticListPage"],
    },
};

export default widgetModule;
